import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from './AdminLayout';
import SafetySeriesPanel from './SafetySeriesPanel';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
const SERVER_BASE = API_BASE.replace('/api', '');

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function formatDate(str) {
  if (!str) return '';
  return new Date(str + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    let vid = u.searchParams.get('v');
    if (!vid && u.hostname === 'youtu.be') vid = u.pathname.slice(1);
    if (!vid && u.pathname.includes('/embed/')) vid = u.pathname.split('/embed/')[1];
    return vid || null;
  } catch { return null; }
}

function TypeBadge({ type }) {
  const bg = type === 'video' ? '#fee2e2' : '#dbeafe';
  const color = type === 'video' ? '#dc2626' : '#1d4ed8';
  return (
    <span style={{ background: bg, color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {type}
    </span>
  );
}

// ── Repository card thumbnail ─────────────────────────────────────────────────
function ItemThumbnail({ item }) {
  if (item.type === 'video') {
    const vid = getYouTubeId(item.url);
    if (vid) {
      return (
        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000', borderRadius: '10px 10px 0 0', overflow: 'hidden' }}>
          <img
            src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`}
            alt={item.title}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 44, height: 44, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="white"><path d="M4 3l10 5-10 5V3z"/></svg>
            </div>
          </div>
        </div>
      );
    }
  }
  if (item.type === 'pdf') {
    return (
      <div style={{ width: '100%', paddingBottom: '56.25%', position: 'relative', background: '#f1f5f9', borderRadius: '10px 10px 0 0', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
            <rect width="40" height="48" rx="4" fill="#e2e8f0"/>
            <path d="M8 36h24M8 28h24M8 20h14" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
            <path d="M26 2v10h10" fill="none" stroke="#94a3b8" strokeWidth="1.5"/>
            <path d="M26 2L36 12H26V2z" fill="#cbd5e1"/>
          </svg>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: 0.5 }}>PDF</span>
        </div>
      </div>
    );
  }
  return <div style={{ width: '100%', paddingBottom: '56.25%', background: '#f3f4f6', borderRadius: '10px 10px 0 0' }} />;
}

// ── Simple calendar ───────────────────────────────────────────────────────────
function CalendarView({ assignments, onClickAssignment, upcoming = [] }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = new Date(year, month).toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Group assignments by due_date day for this month/year
  const byDay = {};
  assignments.forEach(a => {
    const d = new Date(a.due_date + 'T12:00:00');
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(a);
    }
  });

  // The weekly safety videos still to come, on the day each one plays, with its cover.
  const comingByDay = {};
  upcoming.forEach(u => {
    const d = new Date(u.date + 'T12:00:00');
    if (d.getFullYear() === year && d.getMonth() === month) { const day = d.getDate(); if (!comingByDay[day]) comingByDay[day] = []; comingByDay[day].push(u); }
  });
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div>
      {/* Nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={() => { const d = new Date(year, month - 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }}
          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 15, minWidth: 160, textAlign: 'center' }}>{monthName}</span>
        <button onClick={() => { const d = new Date(year, month + 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }}
          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>›</button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#9ca3af', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => (
          <div key={i} style={{
            minHeight: 80, background: day ? '#fff' : 'transparent', borderRadius: 8,
            border: day ? '1px solid #e5e7eb' : 'none',
            padding: day ? '6px 6px 4px' : 0,
            position: 'relative',
          }}>
            {day && (
              <>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: isToday(day) ? '#fff' : '#374151',
                  background: isToday(day) ? '#f97316' : 'transparent',
                  width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 4,
                }}>{day}</div>
                {(comingByDay[day] || []).map(u => (
                  <a key={'up-' + u.id} href={u.url} target="_blank" rel="noreferrer" title={`Plays ${new Date(u.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} · sign-off due ${new Date(u.due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    style={{ display: 'block', marginBottom: 3, borderRadius: 6, overflow: 'hidden', border: '1px solid #fde68a', background: '#fffbeb', textDecoration: 'none' }}>
                    {u.thumb && <img src={u.thumb} alt="" style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }} />}
                    <div style={{ padding: '3px 5px', fontSize: 10.5, fontWeight: 800, color: '#92400e', lineHeight: 1.2 }}>▶ {u.title}</div>
                  </a>
                ))}
                {(byDay[day] || []).map(a => (
                  <button key={a.id} onClick={() => onClickAssignment(a)}
                    style={{ display: 'block', width: '100%', marginBottom: 2, padding: '2px 4px', borderRadius: 4, border: 'none', background: a.type === 'video' ? '#fee2e2' : '#dbeafe', color: a.type === 'video' ? '#dc2626' : '#1d4ed8', fontSize: 10, fontWeight: 600, cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.title}
                  </button>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Assignment completions modal (with who hasn't completed) ──────────────────
function CompletionsModal({ assignment, allWorkers, onClose }) {
  const [completions, setCompletions] = useState(null);
  const [sigModal, setSigModal] = useState(null);
  // Who it was FOR — the crew for a weekly video — so "who hasn't" is the right list.
  const [audience, setAudience] = useState(null);

  useEffect(() => {
    apiFetch(`/safety/assignments/${assignment.id}/completions`).then(setCompletions);
    apiFetch(`/safety/assignments/${assignment.id}/audience`).then(a => setAudience(Array.isArray(a?.people) ? a.people : null)).catch(() => setAudience(null));
  }, [assignment.id]);

  const completedIds = new Set((completions || []).map(c => c.user_id));
  const notCompleted = (audience || allWorkers).filter(w => !completedIds.has(w.id));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 600, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <TypeBadge type={assignment.type} />
              <span style={{ fontWeight: 700, fontSize: 16 }}>{assignment.title}</span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Due {formatDate(assignment.due_date)}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', padding: '0 4px' }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>
          {!completions ? (
            <p style={{ color: '#9ca3af' }}>Loading…</p>
          ) : (
            <div style={{ display: 'flex', gap: 24 }}>
              {/* Completed */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: '#dcfce7', borderRadius: 99, padding: '2px 8px' }}>✓ COMPLETED ({completions.length})</span>
                </div>
                {completions.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#9ca3af' }}>None yet.</p>
                ) : completions.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(c.completed_at).toLocaleString()}</div>
                    </div>
                    <img src={c.signature} alt="sig" onClick={() => setSigModal(c.signature)}
                      style={{ height: 32, border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', background: '#fff' }} />
                  </div>
                ))}
              </div>

              {/* Not completed */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 10 }}>
                  <span style={{ background: '#fee2e2', borderRadius: 99, padding: '2px 8px' }}>✗ PENDING ({notCompleted.length})</span>
                </div>
                {notCompleted.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>Everyone's done! 🎉</p>
                ) : notCompleted.map(w => (
                  <div key={w.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{w.firstName} {w.lastName}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {sigModal && (
        <div onClick={() => setSigModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={sigModal} alt="signature" style={{ maxWidth: '80vw', maxHeight: '80vh', background: '#fff', padding: 16, borderRadius: 12 }} />
        </div>
      )}
    </div>
  );
}

// ── Item form modal ───────────────────────────────────────────────────────────
function ItemFormModal({ item, onClose, onSave }) {
  const [title, setTitle] = useState(item?.title || '');
  const [type, setType] = useState(item?.type || 'video');
  const [url, setUrl] = useState(item?.url || '');
  const [description, setDescription] = useState(item?.description || '');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) return setErr('Title required');
    if (type === 'video' && !url.trim()) return setErr('URL required for video');
    if (type === 'pdf' && !item && !file) return setErr('PDF file required');
    setSaving(true); setErr('');
    try {
      if (item) {
        const saved = await apiFetch(`/safety/items/${item.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, type, url: type === 'video' ? url : item.url, description }),
        });
        onSave(saved);
      } else {
        const fd = new FormData();
        fd.append('title', title); fd.append('type', type); fd.append('description', description);
        if (type === 'video') fd.append('url', url);
        if (type === 'pdf' && file) fd.append('file', file);
        const saved = await apiFetch('/safety/items', { method: 'POST', body: fd });
        onSave(saved);
      }
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 480, maxWidth: '95vw' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>{item ? 'Edit Item' : 'Add Safety Item'}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>TITLE</label>
            <input value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>TYPE</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['video', 'pdf'].map(t => (
                <button type="button" key={t} onClick={() => setType(t)}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid', borderColor: type === t ? '#f97316' : '#e5e7eb', background: type === t ? '#fff7ed' : '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: type === t ? '#f97316' : '#374151' }}>
                  {t === 'video' ? '▶ Video' : '📄 PDF'}
                </button>
              ))}
            </div>
          </div>
          {type === 'video' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>VIDEO URL (YouTube)</label>
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          )}
          {type === 'pdf' && !item && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>PDF FILE</label>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13 }} />
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>DESCRIPTION (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          {err && <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Assign modal ──────────────────────────────────────────────────────────────
function AssignModal({ items, onClose, onSave }) {
  const [itemId, setItemId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!itemId || !dueDate) return;
    setSaving(true);
    try {
      const saved = await apiFetch('/safety/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ safetyItemId: itemId, dueDate }),
      });
      onSave(saved);
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 420, maxWidth: '95vw' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Assign Safety Item</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>SELECT ITEM</label>
            <select value={itemId} onChange={e => setItemId(e.target.value)} required style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}>
              <option value="">Choose from repository…</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.title} ({i.type})</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>DUE DATE</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 14 }} />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>This will be required for all active workers.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminSafety() { return <SafetyContent />; }

// `embedded` renders the same Safety UI without the AdminLayout chrome, so it
// can live inside another page's tab (e.g. the Shop section).
export function SafetyContent({ embedded = false }) {
  const Wrap = embedded ? React.Fragment : AdminLayout;
  const [tab, setTab] = useState('assignments');
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [calView, setCalView] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [repoSearch, setRepoSearch] = useState('');
  // The weekly videos still to come — drawn on the calendar on the day each plays.
  const [seriesUpcoming, setSeriesUpcoming] = useState([]);
  useEffect(() => { apiFetch('/safety/series').then(r => setSeriesUpcoming(Array.isArray(r.upcoming) ? r.upcoming : [])).catch(() => {}); }, []);

  useEffect(() => {
    Promise.all([
      apiFetch('/safety/items'),
      apiFetch('/safety/assignments'),
      apiFetch('/users'),
    ]).then(([i, a, u]) => {
      setItems(i);
      setAssignments(a);
      setAllWorkers(u.filter(w => w.role === 'Worker'));
    }).finally(() => setLoading(false));
  }, []);

  async function deleteItem(id) {
    if (!window.confirm('Delete this item from the repository?')) return;
    await apiFetch(`/safety/items/${id}`, { method: 'DELETE' });
    setItems(prev => prev.filter(i => i.id !== id));
  }

  async function deleteAssignment(id) {
    if (!window.confirm('Remove this assignment?')) return;
    await apiFetch(`/safety/assignments/${id}`, { method: 'DELETE' });
    setAssignments(prev => prev.filter(a => a.id !== id));
  }

  const filteredItems = items.filter(i =>
    !repoSearch || i.title.toLowerCase().includes(repoSearch.toLowerCase()) || (i.description || '').toLowerCase().includes(repoSearch.toLowerCase())
  );

  return (
    <Wrap>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Safety Training</h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 14 }}>Manage safety requirements for all workers</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setEditItem(null); setShowItemModal(true); }}
            style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            + Add to Repository
          </button>
          <button onClick={() => setShowAssignModal(true)}
            style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            + Assign Requirement
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        {[['assignments', 'Active Assignments'], ['repository', 'Repository']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '10px 20px', border: 'none', borderBottom: tab === key ? '2px solid #f97316' : '2px solid transparent', background: 'none', fontWeight: tab === key ? 700 : 500, color: tab === key ? '#f97316' : '#6b7280', cursor: 'pointer', fontSize: 14 }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          {/* ── Assignments tab ── */}
          {tab === 'assignments' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3, gap: 2 }}>
                  <button onClick={() => setCalView(false)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: !calView ? '#fff' : 'transparent', fontWeight: 600, fontSize: 12, cursor: 'pointer', boxShadow: !calView ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', color: !calView ? '#374151' : '#6b7280' }}>
                    ☰ List
                  </button>
                  <button onClick={() => setCalView(true)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: calView ? '#fff' : 'transparent', fontWeight: 600, fontSize: 12, cursor: 'pointer', boxShadow: calView ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', color: calView ? '#374151' : '#6b7280' }}>
                    📅 Calendar
                  </button>
                </div>
              </div>

              {calView ? (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
                  <CalendarView assignments={assignments} onClickAssignment={setSelectedAssignment} upcoming={seriesUpcoming} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {assignments.length === 0 && <p style={{ color: '#9ca3af', fontSize: 14 }}>No assignments yet.</p>}
                  {assignments.map(a => (
                    <div key={a.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20, cursor: 'pointer' }}
                      onClick={() => setSelectedAssignment(a)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <TypeBadge type={a.type} />
                          <span style={{ fontWeight: 600, fontSize: 15 }}>{a.title}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>Due {formatDate(a.due_date)}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: 99 }}>{a.completionCount} completed</span>
                          <button onClick={e => { e.stopPropagation(); deleteAssignment(a.id); }}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>×</button>
                        </div>
                      </div>
                      {a.description && <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>{a.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Repository tab ── */}
          {tab === 'repository' && (
            <>
              {/* The weekly series from Trello, posted one a week (Ryan, Oct 2 2026). */}
              <SafetySeriesPanel onSeries={r => setSeriesUpcoming(Array.isArray(r.upcoming) ? r.upcoming : [])} onChanged={() => Promise.all([apiFetch('/safety/items'), apiFetch('/safety/assignments')]).then(([i, a]) => { setItems(i); setAssignments(a); }).catch(() => {})} />
              <div style={{ marginBottom: 16 }}>
                <input
                  value={repoSearch}
                  onChange={e => setRepoSearch(e.target.value)}
                  placeholder="Search repository…"
                  style={{ width: '100%', maxWidth: 360, border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 14px', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              {filteredItems.length === 0 && (
                <p style={{ color: '#9ca3af', fontSize: 14 }}>{repoSearch ? 'No matches found.' : 'No items yet. Add one with "+ Add to Repository".'}</p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {filteredItems.map(item => (
                  <div key={item.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <ItemThumbnail item={item} />
                    <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <TypeBadge type={item.type} />
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111', lineHeight: 1.3 }}>{item.title}</div>
                      {item.description && <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>{item.description}</div>}
                      <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 10 }}>
                        <button onClick={() => { setEditItem(item); setShowItemModal(true); }}
                          style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Edit
                        </button>
                        <button onClick={() => deleteItem(item.id)}
                          style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showItemModal && (
        <ItemFormModal
          item={editItem}
          onClose={() => { setShowItemModal(false); setEditItem(null); }}
          onSave={saved => {
            setItems(prev => editItem ? prev.map(i => i.id === saved.id ? saved : i) : [saved, ...prev]);
            setShowItemModal(false); setEditItem(null);
          }}
        />
      )}

      {showAssignModal && (
        <AssignModal
          items={items}
          onClose={() => setShowAssignModal(false)}
          onSave={saved => {
            setAssignments(prev => [{ ...saved, completionCount: 0 }, ...prev]);
            setShowAssignModal(false);
          }}
        />
      )}

      {selectedAssignment && (
        <CompletionsModal
          assignment={selectedAssignment}
          allWorkers={allWorkers}
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </Wrap>
  );
}
